/**
 * LanguageSwitcher — seletor de idioma com 2 variantes:
 *   • compact:  3 chips lado a lado (PT/EN/ES) — pro dropdown do avatar
 *   • full:     3 botões grandes com nome do idioma — pro card Aparência
 *
 * Usa setLocale do ThemeProvider (que sincroniza i18next + DB + localStorage).
 */
import { useTheme } from "@/components/ThemeProvider";
import type { LocaleCode } from "@/lib/preferences";
import { useTranslation } from "react-i18next";

const LOCALES: { code: LocaleCode; flag: string; short: string }[] = [
  { code: "pt", flag: "🇧🇷", short: "PT" },
  { code: "en", flag: "🇺🇸", short: "EN" },
  { code: "es", flag: "🇪🇸", short: "ES" },
];

export function LanguageSwitcher({ variant = "compact" }: { variant?: "compact" | "full" }) {
  const { locale, setLocale } = useTheme();
  const { t } = useTranslation();

  if (variant === "compact") {
    return (
      <div className="grid grid-cols-3 gap-1 p-0.5 rounded-lg bg-app-soft">
        {LOCALES.map(o => {
          const active = locale === o.code;
          return (
            <button
              key={o.code}
              onClick={() => setLocale(o.code)}
              aria-label={t(`languages.${o.code}`)}
              aria-pressed={active}
              title={t(`languages.${o.code}`)}
              className={`
                flex items-center justify-center gap-1 py-1.5 rounded-md text-[11px] font-medium
                transition-colors duration-150
                ${active
                  ? "bg-accent-soft text-accent"
                  : "text-app-3 hover:text-app-1"}
              `}
            >
              <span aria-hidden>{o.flag}</span>
              {o.short}
            </button>
          );
        })}
      </div>
    );
  }

  // full
  return (
    <div className="grid grid-cols-3 gap-2">
      {LOCALES.map(o => {
        const active = locale === o.code;
        return (
          <button
            key={o.code}
            onClick={() => setLocale(o.code)}
            aria-pressed={active}
            className={`
              flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all
              ${active
                ? "border-accent bg-accent-soft"
                : "border-app hover:border-app-strong hover:bg-app-hover"}
            `}
          >
            <span className="text-2xl leading-none" aria-hidden>{o.flag}</span>
            <div className="text-[13px] font-semibold text-app-1">{t(`languages.${o.code}`)}</div>
            <div className="text-[10px] text-app-3 uppercase tracking-wider">{o.short}</div>
          </button>
        );
      })}
    </div>
  );
}
